"use client";

import React, { useEffect, useState } from "react";
import { Button } from "@/shadcn-components/ui/button";
import { cn } from "@/lib/utils";
import { useVybeAuthStore } from "@/lib/vybe-auth";
import { fetchAuthProviders } from "@/lib/vybe-auth/api";

/**
 * "Continue with Google" door for the terminal's signed-out cards (proposal 71). Opens the CRM OAuth
 * popup, redeems the handoff code it posts back, and lands in the same session the email + code flow
 * produces. Renders nothing when the CRM has no Google client configured, so those deployments keep the
 * email-only card they have today.
 */

/** Asked once per page load and shared by every card that renders this button. */
let providersPromise: Promise<{ google: boolean }> | null = null;
function googleAvailable(): Promise<boolean> {
  providersPromise ??= fetchAuthProviders();
  return providersPromise.then((p) => p.google).catch(() => false);
}

/** Google's four-colour "G", inline so it needs no network fetch or asset pipeline. */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  );
}

export function GoogleSignInButton({
  compact = false,
  className,
}: {
  /** Header-popover density (h-8) vs the roomier sidebar/mobile gate. */
  compact?: boolean;
  className?: string;
}) {
  const signInWithGoogle = useVybeAuthStore((s) => s.signInWithGoogle);
  const pending = useVybeAuthStore((s) => s.status === "google-pending");
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let alive = true;
    void googleAvailable().then((ok) => {
      if (alive) setAvailable(ok);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!available) return null;

  return (
    <div className={cn("w-full space-y-2", className)}>
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        className={cn("w-full gap-2", compact ? "h-8 text-xs" : "text-sm")}
        disabled={pending}
        onClick={() => void signInWithGoogle()}
      >
        <GoogleMark className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        {pending ? "Waiting for Google…" : "Continue with Google"}
      </Button>

      <div className="flex items-center gap-2">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

export default GoogleSignInButton;
